<template>
  <div class="bg-white">
    <LazyHydrate when-idle>
      <HelpButton :language="language.lang" />
    </LazyHydrate>
    <!-- Content -->
    <LazyHydrate when-idle>
      <Content />
    </LazyHydrate>
    <!-- ./Content -->

    <div class="bg-silver-100">
      <div class="xs:container px-5 sm:px-10 bg-silver-100 lg:flex gap-x-12">
        <LazyHydrate when-idle>
          <SupportV2 />
        </LazyHydrate>
        <LazyHydrate when-idle>
          <HelpTeam class="w-5/12" />
        </LazyHydrate>
        <LazyHydrate when-idle>
          <ParkosTeam class="pt-10 md:pt-20 flex justify-between lg:hidden" />
        </LazyHydrate>
      </div>
    </div>
  </div>
</template>

<script>
import LazyHydrate from 'vue-lazy-hydration';
import Content from '~/components/page-not-found/Content.vue';
import ParkosTeam from '~/components/support/parkosTeam.vue';
import HelpTeam from '~/components/support/helpTeam.vue';
import SupportV2 from '~/components/support/Supportv2.vue';
import HelpButton from '~/components/airport/HelpButton.vue';

export default {
  components: {
    LazyHydrate,
    HelpButton,
    Content,
    SupportV2,
    ParkosTeam,
    HelpTeam,
  },
  head() {
    const links = [
      {
        rel: 'canonical',
        href: `https://${this.$paths.host}`,
      },
      {
        rel: 'alternate',
        hreflang: this.language.lang === 'en-eu' ? 'en' : this.language.lang,
        href: this.$paths.url(false),
      },
    ];
    const alternates = this.languages
      .filter((l) => l.id !== this.language.id)
      .map((m) => ({
        rel: 'alternate',
        hreflang: m.lang === 'en-eu' ? 'en' : m.lang,
        href: (!m.domain.includes('https://') ? 'https://' : '') + m.domain,
      }));
    links.push(...alternates);

    const script = [];

    if (process.env.DATA_ANALYTICS_BUCKET === '1') {
      const bucketSourcePrefix = process.env.NODE_ENV === 'production' ? '/_nuxt_page_not_found' : '';
      const bucketSource = `${bucketSourcePrefix}/da-bucket/dist/not-found-page/index.js`;

      const bucketScript = {
        src: bucketSource,
        body: true,
      };

      script.push(bucketScript);
    }

    return {
      title: this.homeContent.metatitle,
      htmlAttrs: {
        lang: this.language.lang === 'en-eu' ? 'en-us' : this.language.lang,
      },
      meta: [
        {
          'http-equiv': 'content-language',
          content: this.language.lang === 'en-eu' ? 'en-us' : this.language.lang,
        },
        {
          property: 'og:title',
          content: this.homeContent.metatitle,
        },
        {
          name: 'description',
          content: this.homeContent.metadescription,
        },
        {
          property: 'og:description',
          content: this.homeContent.metadescription,
        },
        {
          name: 'twitter:card',
          content: 'summary',
        },
        {
          name: 'twitter:title',
          content: this.homeContent.metatitle,
        },
        {
          name: 'twitter:site',
          content: '@ParkosInt',
        },
        {
          name: 'twitter:creator',
          content: '@ParkosInt',
        },
        {
          name: 'twitter:description',
          content: this.homeContent.metadescription,
        },
        {
          name: 'twitter:image',
          content: `${this.$paths.assetsUrl}img/parkos_twitter_hero.png`,
        },
        {
          property: 'og:type',
          content: 'place',
        },
        {
          property: 'og:locale',
          content: this.language.lang === 'en-eu' ? 'en-us' : this.language.lang,
        },
        {
          property: 'og:url',
          content: `${this.$paths.url(false)}${this.$route?.path}`,
        },
        {
          name: 'og:image',
          content: `${this.$paths.assetsUrl}img/parkos_twitter_hero.png`,
        },
      ],
      link: links,
      script,
      __dangerouslyDisableSanitizersByTagID: {
        datalayer: ['innerHTML'],
      },
    };
  },
  computed: {
    gtmKey() {
      return this.language.gtm_key;
    },
    homeContent() {
      return this.$store.state.pageContent.home || {};
    },
    language() {
      return this.$store.state.language;
    },
    languages() {
      return this.$store.state.languages;
    },
  },
  created() {
    if (process.client) {
      // eslint-disable-next-line nuxt/no-globals-in-created
      window.addEventListener('error', (error) => {
        try {
          // prevent doublefault
          this.$store.commit('bucket/update', {
            key: 'error_line',
            value: error.lineno,
          });
          this.$store.commit('bucket/update', {
            key: 'error_url',
            // eslint-disable-next-line no-restricted-globals
            value: location.href,
          });
          this.$store.commit('bucket/update', {
            key: 'error_message',
            value: error.message,
          });
        } catch (ex) {}
      });
    }
  },
  jsonld() {
    return {
      '@context': 'http://schema.org',
      '@type': 'Organization',
      name: 'Parkos',
      description: this.homeContent.metadescription, // <- Meta description of Home Page
      foundingDate: '2014',
      founders: [
        {
          '@type': 'Person',
          name: 'Peter Bosma, Arne Bos',
        },
      ],
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Boumaboulevard 77',
        addressLocality: 'Groningen',
        addressRegion: 'GR',
        postalCode: '9723 ZS',
        addressCountry: 'NL',
      },
      alternateName: 'Parkos B.V.',
      url: this.$paths.host, // current domain here
      logo: 'https://assets.parkos.com/assets/img/logos/logo-vierkant.png',
      contactPoint: [
        {
          // contact info for all regions, should be kept as full list for all domains
          '@type': 'ContactPoint',
          telephone: '050 808 0177',
          email: 'support@parkos.nl',
          contactType: 'customer service',
          areaServed: 'NL',
          availableLanguage: 'nl',
        },
        {
          '@type': 'ContactPoint',
          telephone: '02 588 97 47',
          email: 'support@parkos.nl',
          contactType: 'customer service',
          areaServed: 'BE',
          availableLanguage: 'nl',
        },
        {
          '@type': 'ContactPoint',
          telephone: '0 800 90 05 83',
          email: 'support@parkos.fr',
          contactType: 'customer service',
          areaServed: 'BE',
          availableLanguage: 'fr',
        },
        {
          '@type': 'ContactPoint',
          telephone: '0061 2 7908 9702',
          email: 'support@parkos.com',
          contactType: 'customer service',
          areaServed: 'AU',
          availableLanguage: 'en',
        },
        {
          '@type': 'ContactPoint',
          telephone: '0800 1810 516',
          email: 'support@parkos.de',
          contactType: 'customer service',
          areaServed: 'AT',
          availableLanguage: 'de',
        },
        {
          '@type': 'ContactPoint',
          telephone: '0044 330 818 7958',
          email: 'support@parkos.com',
          contactType: 'customer service',
          areaServed: 'DK',
          availableLanguage: 'en',
        },
        {
          '@type': 'ContactPoint',
          telephone: '0800 1810 516',
          email: 'support@parkos.de',
          contactType: 'customer service',
          areaServed: 'DE',
          availableLanguage: 'de',
        },
        {
          '@type': 'ContactPoint',
          telephone: '0034 911 23 05 09',
          email: 'support@parkos.es',
          contactType: 'customer service',
          areaServed: 'ES',
          availableLanguage: 'es',
        },
        {
          '@type': 'ContactPoint',
          telephone: '0044 330 818 7958',
          email: 'support@parkos.com',
          contactType: 'customer service',
          areaServed: '150',
          availableLanguage: 'en',
        },
        {
          '@type': 'ContactPoint',
          telephone: '0 800 90 05 83',
          email: 'support@parkos.fr',
          contactType: 'customer service',
          areaServed: 'FR',
          availableLanguage: 'fr',
        },
        {
          '@type': 'ContactPoint',
          telephone: '0039 06 9480 9406',
          email: 'support@parkos.it',
          contactType: 'customer service',
          areaServed: 'IT',
          availableLanguage: 'it',
        },
        {
          '@type': 'ContactPoint',
          telephone: '0031 50 808 0177',
          email: 'support@parkos.com',
          contactType: 'customer service',
          areaServed: 'PL',
          availableLanguage: 'en',
        },
        {
          '@type': 'ContactPoint',
          telephone: '0034 911 23 05 09',
          email: 'support@parkos.es',
          contactType: 'customer service',
          areaServed: 'PT',
          availableLanguage: 'pt',
        },
        {
          '@type': 'ContactPoint',
          telephone: '0044 330 818 7958',
          email: 'support@parkos.se',
          contactType: 'customer service',
          areaServed: 'SE',
          availableLanguage: 'en',
        },
        {
          '@type': 'ContactPoint',
          telephone: '0044 330 818 7958',
          email: 'support@parkos.co.uk',
          contactType: 'customer service',
          areaServed: 'GB',
          availableLanguage: 'en',
        },
        {
          '@type': 'ContactPoint',
          telephone: '001 929 209 5112',
          email: 'support@parkos.com',
          contactType: 'customer service',
          areaServed: 'US',
          availableLanguage: 'en',
        },
      ],
      sameAs: [
        // All websites associated with the Parkos brand, should be kept as full list for all domains
        'https://www.facebook.com/ParkosInternational',
        'https://twitter.com/ParkosInt',
        'https://www.instagram.com/parkos.international/',
        'https://www.youtube.com/channel/UCNu4H1PkMKxmEwrVnFKy4ZA',
        'https://www.trustpilot.com/review/parkos.nl',
        'https://www.trustpilot.com/review/fr.parkos.be',
        'https://www.trustpilot.com/review/parkos.be',
        'https://www.trustpilot.com/review/parkos.co.uk',
        'https://www.trustpilot.com/review/parkos.com',
        'https://www.trustpilot.com/review/parkos.es',
        'https://www.trustpilot.com/review/parkos.fr',
        'https://www.trustpilot.com/review/parkos.it',
        'https://www.trustpilot.com/review/parkos.se',
        'https://www.trustedshops.de/bewertung/info_XAC5772EB37A5389BE138F3AA6AD443EB.html',
        'https://parkos.nl/',
        'https://parkos.com.au/',
        'https://parkos.at/',
        'https://parkos.be/',
        'https://fr.parkos.be/',
        'https://parkos.dk/',
        'https://parkos.de/',
        'https://parkos.es/',
        'https://eu.parkos.com/',
        'https://parkos.fr/',
        'https://parkos.it/',
        'https://parkos.pl/',
        'https://parkos.pt/',
        'https://parkos.se/',
        'https://parkos.co.uk/',
        'https://parkos.com/',
      ],
      aggregateRating: {
        ...(this.reviewsMeta
          ? {
              '@type': 'AggregateRating',
              ratingValue: +this.totalRating,
              bestRating: this.maxRating,
              worstRating: 1,
              reviewCount: this.reviewsMeta.reviews.count,
            }
          : {}),
      },
    };
  },
  mounted() {
    // console.log(window.location.hostname,'ssss')
    this.loadGTM();
    // @ts-ignore
    window.dataLayer = window.dataLayer || [];
    // @ts-ignore
    window.dataLayer.push({
      // @ts-ignore
      cId: this.getCookie('cId'),
      // @ts-ignore
      domainName: this.$paths.host || '',
      pageType: 'home',
      token: '504Z8p2vH6TtWX7BJC0rYaArFl9sYKdAHfisTESx',
    });
    if (this.$paths.host === 'parkos.nl') {
      window.dataLayer = window.dataLayer || [];

      // eslint-disable-next-line no-inner-declarations
      function gtag(...args) {
        window.dataLayer.push(args);
      }

      gtag('js', new Date());

      gtag('config', process.env.GTAG_TRACKING_ID);
    }
    this.setSessionCookies();
  },
  methods: {
    handleReferralCashbackCookie() {
      let cookie = this.getCookie('referral_cashback');
      if (cookie) {
        cookie = JSON.parse(decodeURIComponent(cookie));
      }
      this.$store.commit('referralCashback/set', cookie);
    },
    loadGTM() {
      const { gtmKey } = this;
      if (gtmKey) {
        /* eslint-disable */
        window.loadTagManager = function (w, d, s, l) {
          w[l] = w[l] || [];
          w[l].push({
            'gtm.start': new Date().getTime(),
            event: 'gtm.js',
          });
          var f = d.getElementsByTagName(s)[0],
            j = d.createElement(s),
            dl = l != 'dataLayer' ? '&l=' + l : '';
          j.async = true;
          j.src = `https://www.googletagmanager.com/gtm.js?id=${gtmKey}` + dl;
          f.parentNode.insertBefore(j, f);
        };
        if (typeof tagManagerDelay === 'undefined') {
          window.loadTagManager(window, document, 'script', 'dataLayer');
        }
        /* eslint-enable */
      }
    },
    setSessionCookies() {
      // Tries to set session cookies based on query params and after that retries pushing the cIdto the datalayer if its set in the cId cookie
      const params = this.$route?.query;

      this.$axios({
        method: 'get',
        baseURL: '/',
        url: 'ajax/set-session-cookie',
        params,
      })
        .then(() => {
          window.dataLayer.push({
            cId: this.getCookie('cId'),
          });
        })
        .catch((error) => {
          // Add error handeling for failing session cookie links
          console.log(error); // eslint-disable-line no-console
        })
        .finally(() => {
          this.handleReferralCashbackCookie();
        });
    },
    getCookie(name) {
      const cookie = `; ${document.cookie}`;
      const cookieParts = cookie.split(`; ${name}=`);
      if (cookieParts.length === 2) {
        return cookieParts.pop().split(';').shift();
      }
      return '';
    },
  },
};
</script>

<style lang="scss">
#trustbadge-container-98e3dadd90eb493088abdc5597a70810 {
  z-index: 39 !important;

  ._12n8yed {
    margin-bottom: 30px !important;
  }
}
</style>
